import React, { useEffect, useState, useCallback } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Box, Toolbar, CircularProgress } from '@mui/material';

import PosAppBar from '../components/PosAppBar';
import CloseShiftDialog from './CloseShiftDialog';
import CashMovementDialog from './CashMovementDialog';
import ShiftReportDialog from './ShiftReportDialog';
import { usePosProfile } from './PosProfileContext';
import useSession from '../auth/useSession';

/**
 * Main layout shell for the POS screens.
 * Renders the AppBar and an Outlet for nested routes.
 * Manages the Close Shift dialog.
 */
export default function PosShell() {
  const { profile, setProfile, setProfileData, setOpeningEntry, setCashMovements } = usePosProfile();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useSession();
  const [waitedForProfile, setWaitedForProfile] = useState(false);
  const [closeShiftOpen, setCloseShiftOpen] = useState(false);
  const [cashMovementOpen, setCashMovementOpen] = useState(false);
  const [shiftReportOpen, setShiftReportOpen] = useState(false);

  useEffect(() => {
    if (!profile) {
      const timer = setTimeout(() => setWaitedForProfile(true), 100);
      return () => clearTimeout(timer);
    }
  }, [profile]);

  useEffect(() => {
    if (waitedForProfile && !profile) {
      navigate('/', { replace: true });
    }
  }, [waitedForProfile, profile, navigate]);

  const handleCloseShiftSuccess = useCallback(() => {
    setCloseShiftOpen(false);
    queryClient.setQueryData(['openingEntry', user], []);
    queryClient.removeQueries({ queryKey: ['openingEntry', user] });
    // Clear POS session and go back to profile selection
    setProfile(null);
    setProfileData(null);
    setOpeningEntry(null);
    setCashMovements([]);
    navigate('/', { replace: true });
  }, [navigate, queryClient, setProfile, setProfileData, setOpeningEntry, setCashMovements, user]);

  if (!profile) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress size={36} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <PosAppBar
        onCloseShift={() => setCloseShiftOpen(true)}
        onCashMovement={() => setCashMovementOpen(true)}
        onShiftReport={() => setShiftReportOpen(true)}
      />
      <Toolbar />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 2,
          bgcolor: 'background.default',
        }}
      >
        <Outlet />
      </Box>

      <CashMovementDialog
        open={cashMovementOpen}
        onClose={() => setCashMovementOpen(false)}
      />

      <ShiftReportDialog
        open={shiftReportOpen}
        onClose={() => setShiftReportOpen(false)}
      />

      <CloseShiftDialog
        open={closeShiftOpen}
        onClose={() => setCloseShiftOpen(false)}
        onSuccess={handleCloseShiftSuccess}
      />
    </Box>
  );
}
